import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import { ApiRequest } from '@/utils/axios';
import { IBaseItem } from '@/utils/types/Items';

interface Props {
    selected: IBaseItem[];
    setSelected: (next: IBaseItem[]) => void;
}

const PAGE_SIZE = 12;
// How many numeric page buttons to show on each side of the current page.
const PAGE_WINDOW = 3;

/**
 * Builds a list of page numbers + 'ellipsis' markers for the paginator.
 * Returns a window around the current page plus jumps to the first / last
 * pages. Pure function so it's easy to reason about.
 */
function buildPageNumbers(current: number, total: number): (number | 'ellipsis-left' | 'ellipsis-right')[] {
    if (total <= 1) return [0];
    const windowStart = Math.max(1, current - PAGE_WINDOW);
    const windowEnd = Math.min(total - 2, current + PAGE_WINDOW);

    const pages: (number | 'ellipsis-left' | 'ellipsis-right')[] = [];
    pages.push(0);
    if (windowStart > 1) pages.push('ellipsis-left');
    for (let i = windowStart; i <= windowEnd; i++) pages.push(i);
    if (windowEnd < total - 2) pages.push('ellipsis-right');
    if (total > 1) pages.push(total - 1);
    return pages;
}

const AdminSkinPicker = ({ selected, setSelected }: Props) => {
    const [items, setItems] = useState<IBaseItem[]>([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(0);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);

    const load = useCallback(
        async (targetPage: number, query: string) => {
            setLoading(true);
            try {
                const body: Record<string, unknown> = {
                    page: targetPage + 1,
                    limit: PAGE_SIZE,
                };
                if (query) body.name = query;
                const {
                    data: { response },
                }: { data: { response: { items: IBaseItem[]; count: number } } } =
                    await ApiRequest.post('/admin/items/list', body);
                setItems(response.items || []);
                setCount(response.count || 0);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        },
        []
    );

    useEffect(() => {
        load(0, search);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const t = setTimeout(() => {
            setPage(0);
            load(0, search);
        }, 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    useEffect(() => {
        load(page, search);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
    const pageNumbers = useMemo(() => buildPageNumbers(page, totalPages), [page, totalPages]);

    function countOf(id: number | bigint): number {
        return selected.filter((s) => s.id === id).length;
    }

    function add(item: IBaseItem) {
        setSelected([...selected, item]);
    }
    function remove(id: number | bigint) {
        const idx = selected.findIndex((s) => s.id === id);
        if (idx === -1) return;
        const next = selected.slice();
        next.splice(idx, 1);
        setSelected(next);
    }

    return (
        <div className="flex flex-col gap-3 min-w-0">
            <input
                type="text"
                placeholder="Search item by name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full px-3 py-2 rounded-fl bg-layer_2 border border-stroke text-[12.5px] text-text_main placeholder:text-text_4 outline-none focus:border-accent transition-colors"
            />

            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[420px] overflow-y-auto fluent-scrollbar pr-1">
                {loading && items.length === 0 ? (
                    <div className="col-span-full text-center text-text_3 text-sm py-8">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="col-span-full text-center text-text_3 text-sm py-8">
                        No items found
                    </div>
                ) : (
                    items.map((item) => {
                        const n = countOf(item.id);
                        return (
                            <div
                                key={String(item.id)}
                                className={
                                    'relative flex flex-col gap-1 p-2 rounded-fl border bg-layer_2 transition-colors ' +
                                    (n > 0
                                        ? 'border-accent'
                                        : 'border-stroke hover:border-stroke_strong')
                                }
                            >
                                <button
                                    type="button"
                                    onClick={() => add(item)}
                                    className="flex flex-col items-center gap-1"
                                >
                                    <div className="w-14 h-14 grid place-items-center">
                                        <Image
                                            src={item.icon}
                                            alt=""
                                            width={56}
                                            height={56}
                                            unoptimized
                                        />
                                    </div>
                                    <div className="text-[10.5px] text-text_main truncate w-full text-center">
                                        {item.name}
                                    </div>
                                    <div className="text-[11px] font-bold text-accent_5 tabular-nums">
                                        ${item.priceUsd.toFixed(2)}
                                    </div>
                                </button>
                                {n > 0 && (
                                    <div className="absolute top-1 right-1 flex items-center gap-1 rounded-full bg-layer_3 border border-stroke px-1.5 py-0.5">
                                        <button
                                            type="button"
                                            onClick={() => remove(item.id)}
                                            className="text-text_2 hover:text-text_main text-[12px] leading-none"
                                        >
                                            −
                                        </button>
                                        <span className="text-[11px] font-bold text-text_main tabular-nums min-w-[10px] text-center">
                                            {n}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => add(item)}
                                            className="text-text_2 hover:text-text_main text-[12px] leading-none"
                                        >
                                            +
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {totalPages > 1 && (
                <div className="flex justify-center items-center gap-1 pt-1 flex-wrap">
                    <button
                        type="button"
                        disabled={page === 0}
                        onClick={() => setPage(0)}
                        className="px-2 py-1 rounded-fl-sm text-[12px] border border-stroke bg-layer_2 disabled:opacity-40 hover:bg-layer_3 transition-colors tabular-nums"
                        aria-label="First page"
                    >
                        «
                    </button>
                    <button
                        type="button"
                        disabled={page === 0}
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                        className="px-2 py-1 rounded-fl-sm text-[12px] border border-stroke bg-layer_2 disabled:opacity-40 hover:bg-layer_3 transition-colors"
                        aria-label="Previous page"
                    >
                        ←
                    </button>

                    {pageNumbers.map((n, i) => {
                        if (n === 'ellipsis-left' || n === 'ellipsis-right') {
                            return (
                                <span
                                    key={`${n}-${i}`}
                                    className="px-1 text-[12px] text-text_3 select-none"
                                >
                                    …
                                </span>
                            );
                        }
                        const isActive = n === page;
                        return (
                            <button
                                key={n}
                                type="button"
                                onClick={() => setPage(n)}
                                className={
                                    'min-w-[28px] px-2 py-1 rounded-fl-sm text-[12px] border tabular-nums transition-colors ' +
                                    (isActive
                                        ? 'border-accent bg-accent text-[#0a151c] font-bold'
                                        : 'border-stroke bg-layer_2 hover:bg-layer_3 text-text_2')
                                }
                            >
                                {n + 1}
                            </button>
                        );
                    })}

                    <button
                        type="button"
                        disabled={page >= totalPages - 1}
                        onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                        className="px-2 py-1 rounded-fl-sm text-[12px] border border-stroke bg-layer_2 disabled:opacity-40 hover:bg-layer_3 transition-colors"
                        aria-label="Next page"
                    >
                        →
                    </button>
                    <button
                        type="button"
                        disabled={page >= totalPages - 1}
                        onClick={() => setPage(totalPages - 1)}
                        className="px-2 py-1 rounded-fl-sm text-[12px] border border-stroke bg-layer_2 disabled:opacity-40 hover:bg-layer_3 transition-colors tabular-nums"
                        aria-label="Last page"
                    >
                        »
                    </button>
                </div>
            )}
        </div>
    );
};

export default AdminSkinPicker;
